import { useCallback, useEffect, useState, type RefObject } from 'react'

/**
 * Native fullscreen where the browser allows it (the preview iframe often does
 * not), with a fixed-position fallback so the board can always take over the
 * whole visible area.
 */
export function useFullscreen(ref: RefObject<HTMLElement>) {
  const [native, setNative] = useState(false)
  const [fallback, setFallback] = useState(false)
  const isFullscreen = native || fallback

  useEffect(() => {
    const onChange = () => setNative(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  useEffect(() => {
    if (!fallback) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFallback(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [fallback])

  const toggle = useCallback(async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen()
      } catch {
        /* ignore */
      }
      setFallback(false)
      return
    }
    if (fallback) {
      setFallback(false)
      return
    }
    const el = ref.current
    if (el?.requestFullscreen && document.fullscreenEnabled) {
      try {
        await el.requestFullscreen()
        return
      } catch {
        /* fall through to the CSS fallback */
      }
    }
    setFallback(true)
  }, [fallback, ref])

  return { isFullscreen, isFallback: fallback, toggle }
}
